// Truy vấn + hành động. Mọi hành động ghi đi qua đây để: (1) cập nhật lạc quan cho UI mượt,
// (2) làm mới đúng các khoá, (3) báo lỗi thống nhất.
import { useMemo } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { LanDoiPhuTrach } from '../../server/chi-so';
import type { NguoiDung } from '../../server/kieu';
import type { MucChecklist } from '../../server/checklist';
import type { LienKetViec, MaLienKet } from '../../server/lien-ket';
import type { NhomTaiSan, TrangThaiTaiSan } from '../../server/nhom-tai-san';
import { api, gh, KHO, LoiApi, thongDiepLoi, xoaBoNhoEtag } from './api';
import type { BinhLuanGh, IssueGh, NhanGh } from './kieu';
import { phanTichNhanYml, slugTag } from './nhan';
import { BAC_NGAY, type BacNgay } from './loc';
import { goiYSoTrang, napPhanTrang, nhoSoTrang } from './phan-trang';
import { thanSauDuyet, type HanhDongDuyet } from '../../server/duyet';
import { datMucChecklist } from '../../server/muc-checklist';
import { datNgay, doiMucChecklist, ganNguoiLucDong, sinceTronNgay, nhanChoAi, thayTruc, thongTinTt, tuIssue, UU_TIEN, type MucNgay, type TrangThai, type Viec } from './viec';
import { boThongBao, thongBao } from './thong-bao';
import { cauKetQua, chayHoanTac, dangKyThaoTac, HAN_HOAN_TAC_MS, layRa, layRaGanNhat, taoMuc, trucCanDoc, type AnhViec, type MucHoanTac } from './hoan-tac';
import { qKetQua, qKpi, qThongBao } from './truy-van';
import { homNayVn, type KhoangQuota } from '../../server/vclaw-kieu';
import type { TraBoNho, TraHopVclaw, TraQuotaVclaw, TraSuKien, TraViecVclaw } from './vclaw';
import { duongTimChat, type ThamSoTimChat, type TraTimChat } from './vclaw';

export const KHOA = {
  toi: ['toi'],
  nhan: ['nhan'],
  nhanRepo: ['nhan-repo'],
  nguoi: ['nguoi-dung'],
  issues: ['issues'],
  phuTrach: ['phu-trach'],
  nguoiDuyet: ['nguoi-duyet'],
  binhLuan: (so: number) => ['binh-luan', so],
  nhatKy: (so: number) => ['nhat-ky', so],
  con: (so: number) => ['con', so],
  thongTin: (so: number) => ['thong-tin', so],
  cha: (so: number) => ['cha', so],
  lienKet: (so: number) => ['lien-ket', so],
  taiSan: (brand: string) => ['tai-san', brand],
  checklist: (brand: string) => ['checklist', brand],
  lichSuCk: (brand: string, tai_san: string) => ['checklist-lich-su', brand, tai_san],
  lienKetViec: (brand: string) => ['lien-ket-viec', brand],
};

// Tài sản thương hiệu (D1 tai_san — functions/api/tai-san). Việc ↔ tài sản: nhãn `tai-san:<slug>` cùng brand.
export type { MucChecklist, NhomTaiSan, TrangThaiTaiSan, LienKetViec, MaLienKet };
export type TaiSan = {
  id: number; brand: string; slug: string; ten: string; nhom: NhomTaiSan; mo_ta: string; muc_dich?: string; lien_ket: string;
  trang_thai: TrangThaiTaiSan; thu_tu: number; sua_luc: string;
};

// Mô tả NỘI BỘ + người tạo của một việc (D1 — functions/api/viec/[so]/thong-tin.ts).
export type ThongTinViec = {
  mo_ta_noi_bo: { noi_dung: string; sua_luc: string; sua_boi: { id: number; ten: string } } | null;
  nguoi_tao: { id: number; ten: string; tu_dong: boolean };
};

// Người đang đăng nhập + phạm vi xem (server/pham-vi.ts): pham_vi ≠ null → giao diện ở dạng NỘI BỘ BRAND (ẩn mọi thứ
// về AI, brand khác, nhân sự nội bộ). ten_he_thong: tên hiện thay cho admin / nội bộ / AI khi xem trong brand.
export type Toi = NguoiDung & { pham_vi: string | null; ten_he_thong: string; brand_ten: string | null };

// Định nghĩa truy vấn lõi dùng chung giữa hook và napTruoc (cùng khoá + cùng hàm).
const qToi = queryOptions({
  queryKey: KHOA.toi,
  queryFn: async (): Promise<Toi | null> => {
    try {
      const j = await api.get<{ nguoi: NguoiDung; pham_vi?: string | null; ten_he_thong?: string; brand_ten?: string | null }>('toi');
      return { ...j.nguoi, pham_vi: j.pham_vi ?? null, ten_he_thong: j.ten_he_thong || 'Ban điều hành', brand_ten: j.brand_ten ?? null };
    } catch (e) {
      if (e instanceof LoiApi && e.ma === 401) return null;
      throw e;
    }
  },
  staleTime: 5 * 60_000,
});

const qBoNhan = queryOptions({
  queryKey: KHOA.nhan,
  queryFn: async () => phanTichNhanYml(await gh.raw(`repos/${KHO}/contents/nhan.yml`)),
  staleTime: 30 * 60_000,
});

const qNhanRepo = queryOptions({
  queryKey: KHOA.nhanRepo,
  queryFn: async () => {
    // Repo đã có hơn 100 nhãn → đợt đầu gọi sẵn 2 trang cùng lúc (trước đây trang 2 chờ trang 1 về).
    const kq = await napPhanTrang((trang) => gh.get<NhanGh[]>(`repos/${KHO}/labels?per_page=100&page=${trang}`), {
      goi_y: goiYSoTrang('nhan', 2),
      toi_da: 10,
      khoa: (n) => n.name,
    });
    nhoSoTrang('nhan', kq.so_trang);
    if (kq.bi_cat) console.warn('VTask: danh sách nhãn chạm trần 1 000 — nhãn còn lại chưa hiện.');
    return kq.ds;
  },
  staleTime: 10 * 60_000,
});

const qNguoiDung = queryOptions({
  queryKey: KHOA.nguoi,
  queryFn: async () => (await api.get<{ ds: (NguoiDung & { dang_nhap_luc?: string })[] }>('nguoi-dung')).ds,
  staleTime: 5 * 60_000,
});

const qPhuTrach = queryOptions({
  queryKey: KHOA.phuTrach,
  queryFn: async () => (await api.get<{ bang: Record<string, number[]> }>('phu-trach')).bang,
  staleTime: 60_000,
});

// Người duyệt của từng việc (D1 nguoi_duyet — functions/api/nguoi-duyet.ts): { "<số việc>": <id người duyệt> }.
const qNguoiDuyet = queryOptions({
  queryKey: KHOA.nguoiDuyet,
  queryFn: async () => (await api.get<{ bang: Record<string, number> }>('nguoi-duyet')).bang,
  staleTime: 60_000,
});

export const useToi = () => useQuery(qToi);
// Brand người xem bị giới hạn vào (null = thấy tất cả) — mọi chỗ ẩn/hiện của dạng nội bộ brand đọc từ đây.
export const usePhamVi = () => useToi().data?.pham_vi ?? null;
// Tên của một người theo id: người trong danh sách (người trong brand chỉ nhận được đồng đội cùng brand) → tên thật;
// không có (admin, nội bộ, AI) → tên hệ thống khi xem trong brand, "#id" khi xem nội bộ.
export function useTenNguoi(): (id: number | null | undefined) => string {
  const toi = useToi().data;
  const ds = useNguoiDung().data;
  return (id) => {
    if (id === null || id === undefined) return toi?.pham_vi ? toi.ten_he_thong : '—';
    const n = ds?.find((x) => x.id === id);
    if (n) return n.ten;
    if (toi && id === toi.id) return toi.ten;
    return toi?.pham_vi ? toi.ten_he_thong : id === 0 ? 'Admin' : `#${id}`;
  };
}
export const useBoNhan = () => useQuery(qBoNhan);
export const useNhanRepo = () => useQuery(qNhanRepo);
export const useNguoiDung = () => useQuery(qNguoiDung);
export const usePhuTrach = () => useQuery(qPhuTrach);
export const useNguoiDuyet = () => useQuery(qNguoiDuyet);

// Các lần đổi người phụ trách (410 ngày) — chỉ trang thống kê cần, để tính việc xong cho người phụ trách LÚC ĐÓNG.
// Khoá bắt đầu bằng KHOA.phuTrach → mọi chỗ làm mới bảng phụ trách sau khi giao việc cũng làm mới luôn cái này.
const qDoiPhuTrach = queryOptions({
  queryKey: [...KHOA.phuTrach, 'lich-su'],
  queryFn: async () => (await api.get<{ doi: Record<string, LanDoiPhuTrach[]> }>('phu-trach?lich_su=1')).doi,
  staleTime: 60_000,
});
export const useDoiPhuTrach = (bat: boolean) => useQuery({ ...qDoiPhuTrach, enabled: bat });

// Trần an toàn của danh sách việc MỞ: 30 trang = 3 000 việc. Trước đây là 5 trang (500 việc) và chạm trần thì việc thứ 501 trở
// đi biến mất khỏi giao diện mà không ai hay (30/09: đã hơn 400 việc mở). Chạm trần mới → báo ở console, không nuốt im.
const TRANG_MO_TOI_DA = 30;

async function napIssues(): Promise<IssueGh[]> {
  // Mọi trang việc MỞ cùng 100 việc ĐÓNG gần nhất gọi SONG SONG: MỘT vòng mạng, không phải 1 + (số trang − 1) vòng nối đuôi
  // (30/09, ~400 việc mở = 5 trang: trang cuối về sau trang đầu 4 vòng mạng). Số trang đợt đầu lấy từ lần tải trước của máy
  // này; máy mới dùng 5 — sát quy mô hiện tại, lần tải đầu tự học lại số thật.
  const [mo, dong] = await Promise.all([
    napPhanTrang((trang) => gh.get<IssueGh[]>(`repos/${KHO}/issues?state=open&per_page=100&page=${trang}`), {
      goi_y: goiYSoTrang('viec-mo', 5),
      toi_da: TRANG_MO_TOI_DA,
      khoa: (i) => i.number,
    }),
    gh.get<IssueGh[]>(`repos/${KHO}/issues?state=closed&per_page=100&sort=updated&direction=desc`),
  ]);
  nhoSoTrang('viec-mo', mo.so_trang);
  if (mo.bi_cat) console.warn(`VTask: danh sách việc mở chạm trần ${TRANG_MO_TOI_DA * 100} — việc cũ hơn chưa hiện. Cần phân trang phía máy chủ.`);
  return [...mo.ds, ...dong].filter((i) => !i.pull_request);
}

const qIssues = queryOptions({ queryKey: KHOA.issues, queryFn: napIssues, staleTime: 60_000, refetchInterval: 120_000 });

export const useIssues = () => useQuery(qIssues);

// Tải trước lúc khởi động, SONG SONG với /api/toi. Nếu không, dữ liệu chỉ bắt đầu tải khi đã biết đăng nhập, chunk
// trang đã về và trang đã mount (đo thật 28/09: 429 ms sau khi mở trang). Chỉ chạy khi trình duyệt này đang có phiên
// ở lần trước (cờ ghiCoPhien). Cờ sai thì các lượt gọi nhận 401 một lần rồi bị bỏ qua: truy vấn lỗi tự tải lại khi
// trang mount sau đăng nhập.
const CO_PHIEN = 'vtask:co-phien';
export function ghiCoPhien(co: boolean) {
  try {
    if (co) localStorage.setItem(CO_PHIEN, '1');
    else localStorage.removeItem(CO_PHIEN);
  } catch { /* chế độ riêng tư / chặn lưu trữ → chỉ mất phần tải trước */ }
}
export function napTruoc(qc: QueryClient) {
  // /api/toi đi TRƯỚC: app chưa vẽ được gì khi chưa có nó (trên HTTP/1.1 trình duyệt chỉ mở 6 kết nối mỗi máy chủ,
  // xếp sau 8 lượt kia là chờ thêm ~200 ms — đo thật ở local).
  void qc.prefetchQuery(qToi);
  let co = false;
  try { co = localStorage.getItem(CO_PHIEN) === '1'; } catch { /* như trên */ }
  if (!co) return;
  void qc.prefetchQuery(qIssues);
  void qc.prefetchQuery(qPhuTrach);
  void qc.prefetchQuery(qNguoiDuyet);
  void qc.prefetchQuery(qNguoiDung);
  void qc.prefetchQuery(qBoNhan);
  void qc.prefetchQuery(qNhanRepo);
  void qc.prefetchQuery(qKetQua);
  void qc.prefetchQuery(qKpi);
  void qc.prefetchQuery(qThongBao);
  // Lịch sử 70 ngày + lịch sử đổi người phụ trách: trang nào cũng xin chúng NGAY SAU khi danh sách việc về (tra việc theo số,
  // thống kê) — để đến lúc đó mới xin là thêm một vòng mạng nối đuôi vào cuối thác nước. Cùng khoá với useViecDaDong(70).
  void qc.prefetchQuery({ queryKey: ['issues-dong', 70], staleTime: 5 * 60_000, queryFn: () => napDaDong(70) });
  void qc.prefetchQuery(qDoiPhuTrach);
}

// Danh sách việc đã ghép người phụ trách. Rỗng khi chưa tải xong.
export function useViec(): { ds: Viec[]; dang_tai: boolean; loi: unknown } {
  const issues = useIssues();
  const pt = usePhuTrach();
  const ds = useMemo(
    () => (issues.data || []).map((i) => tuIssue(i, pt.data || {})),
    [issues.data, pt.data],
  );
  return { ds, dang_tai: issues.isLoading, loi: issues.error };
}

// Việc ĐÃ ĐÓNG trong N ngày qua — cho thống kê năng suất/KPI tự tính (danh sách chính chỉ giữ 100 việc
// đóng gần nhất, không đủ cho số liệu theo tháng). `since` lọc theo ngày cập nhật: việc đóng trong
// khoảng đó chắc chắn được cập nhật trong khoảng đó. Tối đa 10 trang = 1 000 việc.
//
// Cửa sổ theo BẬC (70 · 200 · 400 ngày) và dùng lại bậc lớn hơn nếu đã có trong cache: trang nào cũng
// đòi 400 ngày thì lần tải đầu tốn tới 10 lượt gọi GitHub — đo thật 28/09: trang OKR trên điện thoại còn
// quay khung chờ sau 1,5 giây. 0 = không cần lịch sử (không gọi gì).
// (BAC_NGAY và kiểu BacNgay nằm ở lib/loc.ts: bộ lọc theo kỳ tính cửa sổ cần nạp từ CÙNG danh sách bậc này.)
export type { BacNgay };

// Cửa sổ lịch sử nào đã chạm trần 1 000 việc. Ghi vào bản đồ NGOÀI React, không gắn thuộc tính lên mảng của truy vấn:
// TanStack sao chép mảng khi tải lại và làm rơi thuộc tính lạ. Đọc lúc render (sau khi truy vấn đã về) để màn hình
// báo "số có thể thiếu" thay vì chỉ có một dòng console.warn mà người xem không thấy.
const DONG_BI_CAT = new Map<number, boolean>();

async function napDaDong(so_ngay: number): Promise<IssueGh[]> {
  const tu = sinceTronNgay(so_ngay);
  const ten = `viec-dong-${so_ngay}`;
  const kq = await napPhanTrang((trang) => gh.get<IssueGh[]>(`repos/${KHO}/issues?state=closed&since=${tu}&per_page=100&page=${trang}`), {
    goi_y: goiYSoTrang(ten, 1),
    toi_da: 10,
    khoa: (i) => i.number,
  });
  nhoSoTrang(ten, kq.so_trang);
  DONG_BI_CAT.set(so_ngay, kq.bi_cat);
  if (kq.bi_cat) console.warn(`VTask: việc đã đóng trong ${so_ngay} ngày chạm trần 1 000 — số liệu thống kê thiếu phần cũ nhất.`);
  return kq.ds.filter((i) => !i.pull_request && i.closed_at && i.closed_at >= tu);
}

export function useViecDaDong(so_ngay: BacNgay) {
  const qc = useQueryClient();
  const pt = usePhuTrach();
  // Bậc lớn hơn đã có sẵn → cắt từ đó, khỏi gọi lại.
  const lon_hon = BAC_NGAY.filter((b) => b > so_ngay).find((b) => qc.getQueryData(['issues-dong', b]));
  const q = useQuery({
    queryKey: ['issues-dong', lon_hon ?? so_ngay],
    enabled: so_ngay > 0,
    staleTime: 5 * 60_000,
    queryFn: () => napDaDong(lon_hon ?? so_ngay),
  });
  const ds = useMemo(() => {
    const tu = new Date(Date.now() - so_ngay * 86400_000).toISOString();
    return (q.data || []).filter((i) => i.closed_at! >= tu).map((i) => tuIssue(i, pt.data || {}));
  }, [q.data, pt.data, so_ngay]);
  // `bi_cat`: lịch sử đã nạp chạm trần 1 000 việc → việc đóng cũ nhất của cửa sổ có thể thiếu (nơi hiển thị phải nói ra).
  return { ds, dang_tai: so_ngay > 0 && q.isLoading, bi_cat: so_ngay > 0 && DONG_BI_CAT.get(lon_hon ?? so_ngay) === true };
}

// Mục tiêu (loai:muc-tieu) đã đóng — MỘT lượt gọi lọc theo nhãn, thay vì tải cả lịch sử việc.
export function useMucTieuDaDong() {
  const pt = usePhuTrach();
  const q = useQuery({
    queryKey: ['muc-tieu-dong'],
    staleTime: 5 * 60_000,
    queryFn: () => gh.get<IssueGh[]>(`repos/${KHO}/issues?state=closed&labels=loai:muc-tieu&per_page=100&sort=updated&direction=desc`),
  });
  const ds = useMemo(() => (q.data || []).filter((i) => !i.pull_request).map((i) => tuIssue(i, pt.data || {})), [q.data, pt.data]);
  return { ds, dang_tai: q.isLoading };
}

// Việc để tính năng suất / KPI tự động = đang mở + đã đóng trong cửa sổ `so_ngay`.
export function useViecThongKe(so_ngay: BacNgay = 400): { ds: Viec[]; dang_tai: boolean; bi_cat: boolean } {
  const mo = useViec();
  const dong = useViecDaDong(so_ngay);
  const doi = useDoiPhuTrach(so_ngay > 0);
  const ds = useMemo(() => {
    const co = new Set(dong.ds.map((v) => v.so));
    // Việc đóng gần đây đã nằm trong danh sách chính — bổ sung phần đóng cũ hơn, không trùng.
    return ganNguoiLucDong([...mo.ds.filter((v) => v.mo || !co.has(v.so)), ...dong.ds], doi.data);
  }, [mo.ds, dong.ds, doi.data]);
  // Chờ cả lịch sử phụ trách: không thì số liệu hiện theo người hiện tại rồi mới nhảy sang người lúc đóng.
  return { ds, dang_tai: mo.dang_tai || dong.dang_tai || doi.isLoading, bi_cat: dong.bi_cat };
}

// Một issue lẻ — cho link mở thẳng tới việc không nằm trong danh sách đã tải (đóng từ lâu, trang sau).
export function useMotIssue(so: number | null, bat: boolean) {
  return useQuery({
    queryKey: ['issue', so],
    enabled: !!so && bat,
    queryFn: () => gh.get<IssueGh>(`repos/${KHO}/issues/${so}`),
    retry: false,
  });
}

export function useBinhLuan(so: number | null) {
  return useQuery({
    queryKey: KHOA.binhLuan(so ?? 0),
    enabled: !!so,
    queryFn: () => gh.get<BinhLuanGh[]>(`repos/${KHO}/issues/${so}/comments?per_page=100`),
  });
}

export function useThongTinViec(so: number) {
  return useQuery({ queryKey: KHOA.thongTin(so), queryFn: () => api.get<ThongTinViec>(`viec/${so}/thong-tin`), staleTime: 60_000 });
}

// ── Trạm điều hành VClaw (/vclaw, chỉ admin): hai API CHỈ ĐỌC repo ChatLog. Hàm lọc/nhóm thuần ở lib/vclaw.ts. ──
/** Sự kiện hive mind của một ngày (null = hôm nay giờ VN). Tự làm mới mỗi 30 giây — chỉ khi tab đang hiện và đang xem HÔM NAY (ngày cũ không đổi nữa). */
export function useSuKien(ngay: string | null, bat = true) {
  const xem_hom_nay = ngay === null || ngay === homNayVn();
  return useQuery({
    queryKey: ['vclaw', 'su-kien', ngay ?? 'hom-nay'],
    enabled: bat,
    queryFn: () => api.get<TraSuKien>('vclaw/su-kien' + (ngay ? `?ngay=${encodeURIComponent(ngay)}` : '')),
    refetchInterval: xem_hom_nay ? 30_000 : false,
    refetchIntervalInBackground: false,
    retry: false,
  });
}
/** Sổ cái việc VClaw của một ngày (null = hôm nay giờ VN) — mỗi dòng một việc đã kết thúc, KHÔNG có nội dung tin nhắn. Tên có hậu tố Vclaw vì `useViec` là hook của trang việc.
 *  Tự làm mới 30 giây một lần (chỉ khi tab đang hiện và đang xem HÔM NAY). */
export function useViecVclaw(ngay: string | null, bat = true) {
  const xem_hom_nay = ngay === null || ngay === homNayVn();
  return useQuery({
    queryKey: ['vclaw', 'viec', ngay ?? 'hom-nay'],
    enabled: bat,
    queryFn: () => api.get<TraViecVclaw>('vclaw/viec' + (ngay ? `?ngay=${encodeURIComponent(ngay)}` : '')),
    refetchInterval: xem_hom_nay ? 30_000 : false,
    refetchIntervalInBackground: false,
    retry: false,
  });
}
/** Quota từng hồ sơ theo thời gian (6h / 24h / 7d). Mẫu mới về theo nhịp đo của VClaw (vài phút) → làm mới 60 giây một lần, chỉ khi tab đang hiện. */
export function useQuotaVclaw(khoang: KhoangQuota, bat = true) {
  return useQuery({
    queryKey: ['vclaw', 'quota', khoang],
    enabled: bat,
    queryFn: () => api.get<TraQuotaVclaw>(`vclaw/quota?khoang=${encodeURIComponent(khoang)}`),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  });
}
/** Chỉ mục bộ nhớ của hub (memory/index.json). Đổi chậm (VClaw đẩy lên theo đợt) → 5 phút mới làm mới. */
export function useBoNho(bat = true) {
  return useQuery({ queryKey: ['vclaw', 'bo-nho'], enabled: bat, queryFn: () => api.get<TraBoNho>('vclaw/bo-nho'), staleTime: 5 * 60_000, retry: false });
}
/** Tìm chat nhiều ngày (GET /api/vclaw/tim). `tham_so` = null → KHÔNG gọi (chưa ai bấm Tìm). Chỉ chạy khi tham số đổi hoặc người dùng bấm Tìm (refetch): không tự làm mới, không gọi theo phím gõ.
 *  Mỗi lượt tốn tới ~31 lời gọi GitHub của token dùng chung, nên kết quả giữ 60 giây (cùng từ khoá + khoảng + loại tin không gọi lại), không thử lại khi lỗi. */
export function useTimChatVclaw(tham_so: ThamSoTimChat | null) {
  return useQuery({
    queryKey: ['vclaw', 'tim', tham_so?.q ?? '', tham_so?.ngay ?? 0, tham_so?.loai.join(',') ?? ''],
    enabled: tham_so !== null,
    queryFn: () => api.get<TraTimChat>(duongTimChat(tham_so!)),
    staleTime: 60_000,
    retry: false,
  });
}
/** Biên bản phòng tác chiến của VClaw trong một ngày (null = hôm nay giờ VN) — GET /api/vclaw/hop, CHỈ ĐỌC. Cuộc họp do VClaw ghi lên ChatLog theo nhịp đẩy ~2 phút nên làm mới 60 giây một lần
 *  (chỉ khi tab đang hiện và đang xem HÔM NAY; ngày cũ không đổi nữa). */
export function useHopVclaw(ngay: string | null, bat = true) {
  const xem_hom_nay = ngay === null || ngay === homNayVn();
  return useQuery({
    queryKey: ['vclaw', 'hop', ngay ?? 'hom-nay'],
    enabled: bat,
    queryFn: () => api.get<TraHopVclaw>('vclaw/hop' + (ngay ? `?ngay=${encodeURIComponent(ngay)}` : '')),
    refetchInterval: xem_hom_nay ? 60_000 : false,
    refetchIntervalInBackground: false,
    retry: false,
  });
}

// Việc cha (sub-issue parent) — thường là mục tiêu. GitHub trả 404 khi không có cha → null.
export function useViecCha(so: number) {
  return useQuery({
    queryKey: KHOA.cha(so),
    queryFn: async () => {
      try { return await gh.get<IssueGh>(`repos/${KHO}/issues/${so}/parent`); } catch (e) {
        if (e instanceof LoiApi && e.ma === 404) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

// KR / KPI gắn tay vào việc (D1 lien_ket_okr — functions/api/viec/[so]/lien-ket.ts).
export function useLienKet(so: number) {
  return useQuery({ queryKey: KHOA.lienKet(so), queryFn: () => api.get<{ kr: number[]; kpi: number[] }>(`viec/${so}/lien-ket`), staleTime: 60_000 });
}

// Tài sản của một brand (slug, không có tiền tố `brand:`). null → không tải.
export function useTaiSan(brand: string | null) {
  return useQuery({
    queryKey: KHOA.taiSan(brand || ''),
    queryFn: () => api.get<{ ds: TaiSan[] }>(`tai-san?brand=${encodeURIComponent(brand!)}`).then((j) => j.ds),
    enabled: !!brand,
    staleTime: 60_000,
  });
}

// Checklist hệ thống thương hiệu (D1 checklist_muc — functions/api/checklist). Một lần tải cho cả brand; trang Tài sản
// nhóm theo tài sản, ngăn việc lọc theo số việc.
export function useChecklist(brand: string | null) {
  return useQuery({
    queryKey: KHOA.checklist(brand || ''),
    queryFn: () => api.get<{ ds: MucChecklist[] }>(`checklist?brand=${encodeURIComponent(brand!)}`).then((j) => j.ds),
    enabled: !!brand,
    staleTime: 60_000,
  });
}

// Tick trạng thái một mục: cập nhật lạc quan, lỗi thì trả lại như cũ.
export function useTickMuc() {
  const qc = useQueryClient();
  return async (m: MucChecklist, thay: Partial<Pick<MucChecklist, 'trang_thai' | 'hien_trang' | 'bang_chung' | 'viec' | 'ten' | 'tieu_chi' | 'nhom' | 'cap_do' | 'kenh' | 'noi_dung' | 'thu_tu'>>) => {
    const khoa = KHOA.checklist(m.brand);
    const truoc = qc.getQueryData<MucChecklist[]>(khoa);
    qc.setQueryData<MucChecklist[]>(khoa, (ds) => ds?.map((x) => (x.id === m.id ? { ...x, ...thay } : x)));
    try {
      const r = await api.patch<{ muc: MucChecklist }>(`checklist/${m.id}`, thay);
      qc.setQueryData<MucChecklist[]>(khoa, (ds) => ds?.map((x) => (x.id === m.id ? r.muc : x)));
      return true;
    } catch (e) {
      qc.setQueryData(khoa, truoc);
      thongBao(thongDiepLoi(e), 'loi');
      return false;
    }
  };
}

// Thêm / xoá mục (admin) — làm mới cả bộ checklist của brand.
export function useSuaBoChecklist() {
  const qc = useQueryClient();
  const lam = async <T,>(f: () => Promise<T>, brand: string, bao: string): Promise<T | undefined> => {
    try {
      const kq = await f();
      thongBao(bao);
      return kq;
    } catch (e) {
      thongBao(thongDiepLoi(e), 'loi');
      return undefined;
    } finally {
      void qc.invalidateQueries({ queryKey: KHOA.checklist(brand) });
    }
  };
  return {
    them: (d: { brand: string; tai_san: string; nhom: string; ten: string; tieu_chi?: string; cap_do?: string; kenh?: string; noi_dung?: string }) =>
      lam(() => api.post<{ muc: MucChecklist }>('checklist', d).then((r) => r.muc), d.brand, 'Đã thêm mục.'),
    xoa: (m: MucChecklist) => lam(() => api.del(`checklist/${m.id}`), m.brand, 'Đã xoá mục.'),
  };
}

// Liên kết giữa các khối (việc) của sơ đồ pha × kênh (functions/api/lien-ket). brand null = mọi brand (admin).
export function useLienKetViec(brand: string | null) {
  return useQuery({
    queryKey: KHOA.lienKetViec(brand || ''),
    queryFn: () => api.get<{ ds: LienKetViec[] }>(`lien-ket${brand ? '?brand=' + encodeURIComponent(brand) : ''}`).then((j) => j.ds),
    staleTime: 60_000,
  });
}
export function useSuaLienKet(brand: string | null) {
  const qc = useQueryClient();
  const lam = async <T,>(f: () => Promise<T>, bao: string) => {
    try {
      const kq = await f();
      thongBao(bao);
      return kq;
    } catch (e) {
      thongBao(thongDiepLoi(e), 'loi');
      return undefined;
    } finally {
      void qc.invalidateQueries({ queryKey: ['lien-ket-viec'] });
    }
  };
  return {
    noi: (tu: number, den: number, loai: MaLienKet, ghi_chu = '') =>
      lam(() => api.post<{ lien_ket: LienKetViec }>('lien-ket', { tu, den, loai, ghi_chu, brand: brand ?? '' }).then((r) => r.lien_ket), 'Đã nối hai khối.'),
    go: (id: number) => lam(() => api.del(`lien-ket/${id}`), 'Đã gỡ liên kết.'),
  };
}

// Lịch sử thay đổi checklist của một tài sản (functions/api/checklist/lich-su.ts).
export type DongLichSuCk = { id: number; luc: string; nguoi_dung: number; ma: string; tu: string | null; sang: string | null; truong: string[]; tao: string | null; xoa: string | null };
export function useLichSuChecklist(brand: string | null, tai_san: string | null, bat = true) {
  return useQuery({
    queryKey: KHOA.lichSuCk(brand || '', tai_san || ''),
    queryFn: () => api.get<{ ds: DongLichSuCk[] }>(`checklist/lich-su?brand=${encodeURIComponent(brand!)}&tai_san=${encodeURIComponent(tai_san!)}`).then((j) => j.ds),
    enabled: !!brand && !!tai_san && bat,
    staleTime: 30_000,
  });
}

// Việc theo số: danh sách chính (việc mở + 100 việc đóng gần nhất) + việc đóng trong 70 ngày. undefined = chưa biết
// (đóng lâu hơn / chưa tải) — nơi gọi coi là "không rõ", đừng coi là đã đóng.
export function useViecTheoSo(): (so: number) => Viec | undefined {
  const { ds } = useViec();
  const dong = useViecDaDong(70).ds;
  return useMemo(() => {
    const m = new Map<number, Viec>();
    for (const v of dong) m.set(v.so, v);
    for (const v of ds) m.set(v.so, v);
    return (so: number) => m.get(so);
  }, [ds, dong]);
}

export function useViecCon(so: number | null, bat: boolean) {
  return useQuery({
    queryKey: KHOA.con(so ?? 0),
    enabled: !!so && bat,
    queryFn: () => gh.get<IssueGh[]>(`repos/${KHO}/issues/${so}/sub_issues?per_page=100`),
  });
}

// Theo dõi việc (migration 0003): người theo dõi nhận thông báo khi có comment / đổi trạng thái.
export function useTheoDoi(so: number | null) {
  return useQuery({
    queryKey: ['theo-doi', so ?? 0],
    enabled: !!so,
    queryFn: () => api.get<{ dang_theo_doi: boolean; so_nguoi: number }>(`viec/${so}/theo-doi`),
  });
}

// Tiêu điểm hôm nay: 1–3 việc quan trọng nhất trong ngày của chính mình (giờ VN, server tính ngày).
export function useTieuDiem() {
  return useQuery({ queryKey: ['tieu-diem'], queryFn: () => api.get<{ ngay: string; ds: number[] }>('tieu-diem'), staleTime: 60_000 });
}

/* ═══════════ Hành động ═══════════ */

// Sửa một issue trong bộ nhớ đệm ngay lập tức (lạc quan); lỗi thì hàm gọi tự làm mới lại.
function suaTrongCache(qc: QueryClient, so: number, sua: (i: IssueGh) => IssueGh) {
  qc.setQueryData<IssueGh[]>(KHOA.issues, (cu) => cu?.map((i) => (i.number === so ? sua(i) : i)));
}

// Làm mới NHẸ một việc (nút "Xử lý nhanh" theo dõi lượt của VClaw): MỘT yêu cầu issue lẻ (có ETag nên 304 rất rẻ) vá nhãn/trạng thái vào danh sách đã tải + làm mới bình luận —
// thay vì tải lại CẢ danh sách (≈ 5 trang việc mở + 1 trang việc đóng mỗi lần). Lỗi mạng: bỏ qua, lần sau thử lại.
export async function lamMoiMotViec(qc: QueryClient, so: number): Promise<void> {
  try {
    const moi = await gh.get<IssueGh>(`repos/${KHO}/issues/${so}`);
    suaTrongCache(qc, so, (i) => ({ ...i, labels: moi.labels, state: moi.state, updated_at: moi.updated_at, closed_at: moi.closed_at, comments: moi.comments }));
    qc.setQueryData(['issue', so], moi);
  } catch { /* lần sau thử lại */ }
  await qc.invalidateQueries({ queryKey: KHOA.binhLuan(so) });
}

const nhanGh = (ten: string): NhanGh => ({ name: ten, color: '6b7280' });

// Hàng đợi đánh dấu bước theo từng việc (xem tickBuocNoiBo).
const hang_doi_tick = new Map<number, Promise<boolean>>();

// `xong`: chạy thay cho câu báo khi thành công (dùng để hiện toast có nút Hoàn tác).
async function chay<T>(qc: QueryClient, viec: () => Promise<T>, bao?: string, xong?: () => void): Promise<T | undefined> {
  try {
    const kq = await viec();
    if (xong) xong();
    else if (bao) thongBao(bao);
    return kq;
  } catch (e) {
    thongBao(thongDiepLoi(e), 'loi');
    return undefined;
  } finally {
    qc.invalidateQueries({ queryKey: KHOA.issues });
  }
}

/* ── Hoàn tác (lib/hoan-tac.ts): nút trên toast + Ctrl+Z (App.tsx) ── */

const hoaDau = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Bấm nút "Hoàn tác" trên toast / Ctrl+Z. Lấy RA khỏi ngăn xếp nên một thao tác chỉ chạy một lần.
export function hoanTacTheoId(id: number) {
  const tt = layRa(id);
  if (!tt) { thongBao('Đã quá hạn hoàn tác.', 'loi'); return; }
  void tt.chay();
}
export function hoanTacGanNhat() {
  const tt = layRaGanNhat();
  if (!tt) { thongBao('Không còn thao tác nào để hoàn tác.'); return; }
  void tt.chay();
}

const anhGh = (v: { nhan: string[]; mo: boolean }): AnhViec => ({ nhan: v.nhan, mo: v.mo });

export function useHanhDong() {
  const qc = useQueryClient();

  // Đưa các việc về ảnh trước — đọc dữ liệu MỚI NHẤT (GitHub + D1, không lấy cache giao diện); trục nào đã bị đổi ở nơi khác thì
  // bỏ qua việc đó; ghi bằng đúng các lệnh ghi sẵn có. Đọc trong 10 giây sau lệnh ghi của chính mình thì bộ đệm server luôn hỏi lại
  // GitHub (server/bo-dem.ts VUA_GHI_MS) — cửa sổ hoàn tác 8 giây nằm gọn trong đó.
  const hoanTac = async (mo_ta: string, ds: MucHoanTac[]) => {
    let bang_pt: Promise<Record<string, number[]>> | null = null;
    let bang_nd: Promise<Record<string, number>> | null = null;
    const boc = async <T,>(f: () => Promise<T>) => { try { return await f(); } catch (e) { throw new Error(thongDiepLoi(e)); } };
    const kq = await chayHoanTac(ds, {
      doc: (m) => boc(async () => {
        const can = trucCanDoc(m);
        const a: AnhViec = {};
        if (can.gh) {
          const i = await gh.get<IssueGh>(`repos/${KHO}/issues/${m.so}`);
          a.nhan = i.labels.map((l) => l.name);
          a.mo = i.state === 'open';
        }
        if (can.nguoi) {
          bang_pt ??= api.get<{ bang: Record<string, number[]> }>('phu-trach').then((j) => j.bang);
          a.nguoi = (await bang_pt)[m.so] ?? [];
        }
        if (can.nguoi_duyet) {
          bang_nd ??= api.get<{ bang: Record<string, number> }>('nguoi-duyet').then((j) => j.bang);
          a.nguoi_duyet = (await bang_nd)[m.so] ?? null;
        }
        return a;
      }),
      ghi: (so, l) => boc(async () => {
        if (l.issue) {
          await gh.patch(`repos/${KHO}/issues/${so}`, l.issue);
          const { labels, state } = l.issue;
          suaTrongCache(qc, so, (i) => ({
            ...i,
            ...(labels ? { labels: labels.map((n) => i.labels.find((x) => x.name === n) || nhanGh(n)) } : {}),
            ...(state ? { state, closed_at: state === 'closed' ? new Date().toISOString() : null } : {}),
          }));
        }
        if (l.nguoi) await api.put(`viec/${so}/phu-trach`, { nguoi: l.nguoi });
        if (l.nguoi_duyet !== undefined) await api.put(`viec/${so}/nguoi-duyet`, { nguoi: l.nguoi_duyet });
      }),
    });
    qc.invalidateQueries({ queryKey: KHOA.issues });
    if (ds.some((m) => m.sau.nguoi !== undefined)) qc.invalidateQueries({ queryKey: KHOA.phuTrach });
    if (ds.some((m) => m.sau.nguoi_duyet !== undefined)) qc.invalidateQueries({ queryKey: KHOA.nguoiDuyet });
    for (const m of ds) {
      qc.invalidateQueries({ queryKey: ['issue', m.so] });
      qc.invalidateQueries({ queryKey: KHOA.binhLuan(m.so) });
    }
    const c = cauKetQua(kq, mo_ta);
    thongBao(c.noi_dung, c.kieu);
  };

  // Ghi nhận một thao tác ĐÃ THÀNH CÔNG: toast (câu `bao`, không có thì mô tả) kèm nút "Hoàn tác" trong 8 giây. Không mục nào đổi
  // gì → chỉ báo như cũ, không nút.
  const choHoanTac = (mo_ta: string, muc: (MucHoanTac | null)[], bao?: string) => {
    const ds = muc.filter((m): m is MucHoanTac => !!m);
    if (!ds.length) { if (bao) thongBao(bao); return; }
    let toast = 0;
    const tt = dangKyThaoTac(mo_ta, async () => { boThongBao(toast); await hoanTac(mo_ta, ds); });
    toast = thongBao(bao || hoaDau(mo_ta) + '.', 'thuong', { ms: HAN_HOAN_TAC_MS, nut: { nhan: 'Hoàn tác', mo_ta: 'Hoàn tác: ' + mo_ta, lam: () => hoanTacTheoId(tt.id) } });
  };

  // `ht` = mô tả thao tác để hoàn tác (vd "đổi ưu tiên #12 → P1"); vắng = không cho hoàn tác (vd nhãn của nút "AI làm").
  const datNhan = (v: Viec, nhan: string[], bao?: string, ht?: string) => {
    suaTrongCache(qc, v.so, (i) => ({ ...i, labels: nhan.map((n) => i.labels.find((l) => l.name === n) || nhanGh(n)) }));
    const muc = ht ? taoMuc(v.so, anhGh(v), { nhan, mo: v.mo }) : null;
    return chay(qc, () => gh.patch(`repos/${KHO}/issues/${v.so}`, { labels: nhan }), bao, muc ? () => choHoanTac(ht!, [muc], bao) : undefined);
  };

  const binhLuan = async (so: number, noi_dung: string, nhac: number[] = []) => {
    await api.post(`viec/${so}/binh-luan`, { noi_dung, nhac });
    qc.invalidateQueries({ queryKey: KHOA.binhLuan(so) });
  };
  // Sửa / xoá comment của chính mình (quản trị viên xoá được mọi comment) — luật ở server/quyen.ts.
  const suaBinhLuan = async (so: number, id: number, noi_dung: string) => {
    await api.patch(`viec/${so}/binh-luan/${id}`, { noi_dung });
    qc.invalidateQueries({ queryKey: KHOA.binhLuan(so) });
  };
  const xoaBinhLuan = async (so: number, id: number) => {
    await api.del(`viec/${so}/binh-luan/${id}`);
    qc.invalidateQueries({ queryKey: KHOA.binhLuan(so) });
  };
  const datTheoDoi = async (so: number, bat: boolean) => {
    await api.put(`viec/${so}/theo-doi`, { bat });
    qc.invalidateQueries({ queryKey: ['theo-doi', so] });
  };
  // Bật/tắt một việc trong tiêu điểm hôm nay. Quá 3 → server trả lỗi "qua-3", hàm gọi báo lên cho người dùng.
  const doiTieuDiem = async (so: number) => {
    const cu = qc.getQueryData<{ ngay: string; ds: number[] }>(['tieu-diem'])?.ds ?? (await api.get<{ ds: number[] }>('tieu-diem')).ds;
    const moi = cu.includes(so) ? cu.filter((x) => x !== so) : [...cu, so];
    const kq = await api.put<{ ngay: string; ds: number[] }>('tieu-diem', { ds: moi });
    qc.setQueryData(['tieu-diem'], kq);
    return kq.ds.includes(so);
  };

  // Trục nhãn tự do (tag:, doi-tac:): hiện ngay, tạo nhãn còn thiếu, rồi ghi lên issue.
  const datNhanTuDo = async (v: Viec, tien_to: 'tag' | 'doi-tac' | 'tai-san', gia_tri: string[]) => {
    const ten_truc = { tag: 'tag', 'doi-tac': 'đối tác', 'tai-san': 'tài sản' }[tien_to];
    const p = tien_to + ':';
    const du_kien = [...v.nhan.filter((n) => !n.startsWith(p)), ...[...new Set(gia_tri.map(slugTag).filter(Boolean))].map((t) => p + t)];
    suaTrongCache(qc, v.so, (i) => ({ ...i, labels: du_kien.map((n) => i.labels.find((l) => l.name === n) || nhanGh(n)) }));
    const ten = await damBaoNhan(tien_to, gia_tri);
    return datNhan(v, [...v.nhan.filter((n) => !n.startsWith(p)), ...ten], undefined, `đổi ${ten_truc} #${v.so}`);
  };

  const datMucNgay = (v: Viec, muc: MucNgay, gia_tri: string | null) => {
    const than = datNgay(v.than, muc, gia_tri);
    suaTrongCache(qc, v.so, (i) => ({ ...i, body: than }));
    return chay(qc, () => gh.patch(`repos/${KHO}/issues/${v.so}`, { body: than }));
  };

  return {
    doiTrangThai: (v: Viec, tt: string) => {
      if (tt === 'xong') return dong(v);
      if (!v.mo) {
        suaTrongCache(qc, v.so, (i) => ({ ...i, state: 'open' }));
        const nhan = thayTruc(v.nhan, 'tt:', tt);
        const muc = taoMuc(v.so, anhGh(v), { nhan, mo: true });
        return chay(qc, () => gh.patch(`repos/${KHO}/issues/${v.so}`, { state: 'open', labels: nhan }), `#${v.so} đã mở lại.`,
          () => choHoanTac(`mở lại #${v.so}`, [muc], `#${v.so} đã mở lại.`));
      }
      return datNhan(v, thayTruc(v.nhan, 'tt:', tt), undefined, `đổi trạng thái #${v.so} → ${thongTinTt(tt as TrangThai).ten}`);
    },
    doiUuTien: (v: Viec, uu_tien: string) =>
      datNhan(v, thayTruc(v.nhan, 'uu-tien:', uu_tien), undefined, `đổi ưu tiên #${v.so} → ${UU_TIEN.find((u) => u.ma === uu_tien)?.ngan ?? uu_tien}`),
    // Nhiều nhất MỘT brand (nhan.yml): chọn brand khác là THAY, null là gỡ.
    datBrand: (v: Viec, brand: string | null) => datNhan(v, thayTruc(v.nhan, 'brand:', brand), undefined, brand ? `đổi thương hiệu #${v.so}` : `gỡ thương hiệu #${v.so}`),
    datTags: (v: Viec, tags: string[]) => datNhanTuDo(v, 'tag', tags),
    datDoiTac: (v: Viec, doi_tac: string[]) => datNhanTuDo(v, 'doi-tac', doi_tac),
    datTaiSan: (v: Viec, tai_san: string[]) => datNhanTuDo(v, 'tai-san', tai_san),
    // Pha / kênh: trục có trong nhan.yml (không tạo nhãn mới từ giao diện) — chỉ thay bộ nhãn cùng tiền tố.
    datTruc: (v: Viec, truc: 'pha' | 'kenh', gia_tri: string[]) =>
      datNhan(v, [...v.nhan.filter((n) => !n.startsWith(truc + ':')), ...[...new Set(gia_tri)].map((x) => truc + ':' + x)], undefined, `đổi ${truc === 'pha' ? 'pha' : 'kênh'} #${v.so}`),
    datKy: async (v: Viec, ky: string | null) => {
      if (ky) await damBaoNhan('ky', [ky]);
      return datNhan(v, thayTruc(v.nhan, 'ky:', ky ? 'ky:' + ky : null), undefined, `đổi kỳ OKR #${v.so}`);
    },
    datHan: (v: Viec, han: string | null) => datMucNgay(v, 'Hạn', han),
    datBatDau: (v: Viec, ngay: string | null) => datMucNgay(v, 'Bắt đầu', ngay),
    // Thao tác hàng loạt — tuần tự (GitHub không có API sửa nhiều issue một lượt) và dừng báo lỗi ở việc hỏng.
    // Hoàn tác cả lô: ảnh trước/sau ghi theo TỪNG trục đã ghi xong của từng việc (việc hỏng giữa chừng chỉ hoàn tác phần đã ghi).
    hangLoat: async (ds: Viec[], thay: { tt?: string; uu_tien?: string; them_tag?: string; bo_tag?: string; nguoi?: number[]; nguoi_duyet?: number | null }) => {
      let xong = 0;
      const tag = thay.them_tag ? (await damBaoNhan('tag', [thay.them_tag]))[0] : null;
      const bo = thay.bo_tag ? 'tag:' + thay.bo_tag : null;
      const bang_nd = qc.getQueryData<Record<string, number>>(KHOA.nguoiDuyet) || {};
      const bang_pt = qc.getQueryData<Record<string, number[]>>(KHOA.phuTrach) || {};
      const muc: (MucHoanTac | null)[] = [];
      for (const v of ds) {
        const truoc: AnhViec = {}, sau: AnhViec = {};
        try {
          if (thay.tt === 'xong') {
            await gh.patch(`repos/${KHO}/issues/${v.so}`, { state: 'closed', state_reason: 'completed' });
            Object.assign(truoc, anhGh(v));
            Object.assign(sau, { nhan: v.nhan, mo: false });
          } else {
            let nhan = v.nhan;
            if (thay.tt) nhan = thayTruc(nhan, 'tt:', thay.tt);
            if (thay.uu_tien) nhan = thayTruc(nhan, 'uu-tien:', thay.uu_tien);
            if (tag && !nhan.includes(tag)) nhan = [...nhan, tag];
            if (bo && nhan.includes(bo)) nhan = nhan.filter((n) => n !== bo);
            if (nhan !== v.nhan) {
              await gh.patch(`repos/${KHO}/issues/${v.so}`, { labels: nhan, ...(thay.tt && !v.mo ? { state: 'open' } : {}) });
              // Hiện ngay (ô tag ba trạng thái đọc lại từ cache), khỏi chờ lượt tải lại bên dưới.
              const moi = nhan;
              suaTrongCache(qc, v.so, (i) => ({ ...i, labels: moi.map((n) => i.labels.find((l) => l.name === n) || nhanGh(n)) }));
              Object.assign(truoc, anhGh(v));
              Object.assign(sau, { nhan, mo: thay.tt && !v.mo ? true : v.mo });
            }
          }
          if (thay.nguoi) {
            await api.put(`viec/${v.so}/phu-trach`, { nguoi: thay.nguoi });
            truoc.nguoi = bang_pt[v.so] ?? v.nguoi;
            sau.nguoi = thay.nguoi;
          }
          if (thay.nguoi_duyet !== undefined) {
            await api.put(`viec/${v.so}/nguoi-duyet`, { nguoi: thay.nguoi_duyet });
            truoc.nguoi_duyet = bang_nd[v.so] ?? null;
            sau.nguoi_duyet = thay.nguoi_duyet;
          }
          xong++;
        } catch (e) {
          thongBao(`#${v.so}: ${thongDiepLoi(e)}`, 'loi');
        } finally {
          muc.push(taoMuc(v.so, truoc, sau));
        }
      }
      qc.invalidateQueries({ queryKey: KHOA.issues });
      qc.invalidateQueries({ queryKey: KHOA.phuTrach });
      qc.invalidateQueries({ queryKey: KHOA.nguoiDuyet });
      choHoanTac(`sửa hàng loạt ${ds.length} việc`, muc, `Đã cập nhật ${xong}/${ds.length} việc.`);
    },
    suaNoiDung: (v: Viec, tieu_de: string, than: string) => {
      suaTrongCache(qc, v.so, (i) => ({ ...i, title: tieu_de, body: than }));
      return chay(qc, () => gh.patch(`repos/${KHO}/issues/${v.so}`, { title: tieu_de, body: than }), 'Đã lưu.');
    },
    // Tạo việc con: issue mới kế thừa bối cảnh của cha (lĩnh vực, thương hiệu, kỳ OKR, đối tác, ưu tiên), rồi gắn làm
    // sub-issue của cha. Gắn hỏng thì báo rõ, việc con vẫn còn (không mất việc vừa tạo).
    themViecCon: async (cha: Viec, tieu_de: string): Promise<number | undefined> => {
      const nhan = [
        'loai:viec', 'tt:inbox', 'che-do:duyet', cha.uu_tien || 'uu-tien:p2', 'agent:chung',
        ...(cha.linh_vuc.length ? cha.linh_vuc : ['linh-vuc:ca-nhan']),
        ...(cha.brand ? [cha.brand] : []),
        ...(cha.ky ? [`ky:${cha.ky}`] : []),
        ...cha.doi_tac.map((d) => `doi-tac:${d}`),
      ];
      try {
        const moi = await gh.post<{ id: number; number: number }>(`repos/${KHO}/issues`, { title: tieu_de, body: `Việc con của #${cha.so}.`, labels: nhan });
        try {
          await gh.post(`repos/${KHO}/issues/${cha.so}/sub_issues`, { sub_issue_id: moi.id });
        } catch (e) {
          thongBao(`Đã tạo #${moi.number} nhưng chưa gắn được làm việc con: ${thongDiepLoi(e)}`, 'loi');
        }
        return moi.number;
      } catch (e) {
        thongBao(thongDiepLoi(e), 'loi');
        return undefined;
      } finally {
        qc.invalidateQueries({ queryKey: KHOA.con(cha.so) });
        qc.invalidateQueries({ queryKey: KHOA.issues });
      }
    },
    // Đánh dấu một mục checklist trong mô tả. Đọc thân MỚI NHẤT trước: khác thân đang hiện (ai đó vừa sửa mô tả) thì
    // KHÔNG ghi — thứ tự mục có thể đã đổi, ghi là đánh dấu nhầm mục hoặc đè mất chữ người kia vừa viết.
    doiChecklist: async (v: Viec, thu_tu: number) => {
      const moi = await gh.get<IssueGh>(`repos/${KHO}/issues/${v.so}`).catch(() => null);
      if (moi && (moi.body || '') !== v.than) {
        qc.invalidateQueries({ queryKey: KHOA.issues });
        qc.invalidateQueries({ queryKey: ['issue', v.so] });
        thongBao('Mô tả vừa được sửa ở nơi khác — đã tải bản mới, bấm lại lần nữa.', 'loi');
        return;
      }
      const than = doiMucChecklist(v.than, thu_tu);
      suaTrongCache(qc, v.so, (i) => ({ ...i, body: than }));
      qc.setQueryData<IssueGh>(['issue', v.so], (cu) => (cu ? { ...cu, body: than } : cu));
      await chay(qc, () => gh.patch(`repos/${KHO}/issues/${v.so}`, { body: than }));
    },
    datNguoi: async (v: Viec, nguoi: number[]) => {
      const truoc = qc.getQueryData<Record<string, number[]>>(KHOA.phuTrach)?.[v.so] ?? v.nguoi;
      qc.setQueryData<Record<string, number[]>>(KHOA.phuTrach, (cu) => ({ ...(cu || {}), [v.so]: nguoi }));
      try {
        await api.put(`viec/${v.so}/phu-trach`, { nguoi });
        choHoanTac(`đổi người phụ trách #${v.so}`, [taoMuc(v.so, { nguoi: truoc }, { nguoi })]);
      } catch (e) {
        thongBao(thongDiepLoi(e), 'loi');
      }
      qc.invalidateQueries({ queryKey: KHOA.phuTrach });
      qc.invalidateQueries({ queryKey: KHOA.binhLuan(v.so) });
    },
    // Người duyệt của việc (một người; null = gỡ). Hiện ngay, lỗi thì báo và tải lại.
    datNguoiDuyet: async (v: Viec, nguoi: number | null) => {
      const truoc = qc.getQueryData<Record<string, number>>(KHOA.nguoiDuyet)?.[v.so] ?? null;
      qc.setQueryData<Record<string, number>>(KHOA.nguoiDuyet, (cu) => {
        const c = { ...(cu || {}) };
        if (nguoi === null) delete c[v.so]; else c[v.so] = nguoi;
        return c;
      });
      try {
        await api.put(`viec/${v.so}/nguoi-duyet`, { nguoi });
        choHoanTac(`đổi người duyệt #${v.so}`, [taoMuc(v.so, { nguoi_duyet: truoc }, { nguoi_duyet: nguoi })]);
      } catch (e) {
        thongBao(thongDiepLoi(e), 'loi');
      }
      qc.invalidateQueries({ queryKey: KHOA.nguoiDuyet });
      qc.invalidateQueries({ queryKey: KHOA.binhLuan(v.so) });
    },
    // Người duyệt bấm Duyệt / Trả lại (server làm hộ — functions/api/viec/[so]/duyet.ts). true = thành công.
    duyetViec: async (v: Viec, hanh_dong: HanhDongDuyet, ghi_chu: string): Promise<boolean> => {
      try {
        await api.post(`viec/${v.so}/duyet`, { hanh_dong, ghi_chu });
        // Server đã đổi xong → áp NGAY đúng thay đổi đó (cùng hàm thanSauDuyet server dùng) vào bộ nhớ đệm: số "Cần duyệt" và danh
        // sách giảm liền, không đứng nguyên vài giây chờ tải lại cả kho việc. (Áp SAU khi server xác nhận, không đoán trước: lỗi thì
        // không có gì phải hoàn lại, và dải duyệt trong ngăn việc không nháy tắt trong lúc chờ.)
        suaTrongCache(qc, v.so, (i) => {
          const than = thanSauDuyet(hanh_dong, i.labels.map((l) => l.name));
          if (than.state === 'closed') return { ...i, state: 'closed', closed_at: new Date().toISOString() };
          return { ...i, labels: (than.labels as string[]).map((n) => i.labels.find((l) => l.name === n) || nhanGh(n)) };
        });
        // Hoàn tác chỉ đưa NHÃN / trạng thái về như trước; bình luận + thông báo server đã gửi thì vẫn còn.
        const than = thanSauDuyet(hanh_dong, v.nhan);
        const sau: AnhViec = than.state === 'closed' ? { nhan: v.nhan, mo: false } : { nhan: than.labels as string[], mo: true };
        const ten = { 'duyet-dong': 'duyệt & đóng', 'duyet-tiep': 'duyệt', 'tra-lai': 'trả lại' }[hanh_dong];
        choHoanTac(`${ten} #${v.so} (bình luận vẫn giữ)`, [taoMuc(v.so, anhGh(v), sau)],
          hanh_dong === 'duyet-dong' ? `#${v.so} đã duyệt & đóng.` : hanh_dong === 'duyet-tiep' ? `#${v.so} đã duyệt — làm tiếp.` : `#${v.so} đã trả lại.`);
        return true;
      } catch (e) {
        thongBao(thongDiepLoi(e), 'loi');
        return false;
      } finally {
        qc.invalidateQueries({ queryKey: KHOA.issues });
        qc.invalidateQueries({ queryKey: KHOA.binhLuan(v.so) });
      }
    },
    // Đánh dấu một bước “- [ ]” trong MÔ TẢ NỘI BỘ. Hiện ngay (lạc quan); server so `sua_luc` với bản đang lưu nên các lần bấm
    // của CÙNG một việc xếp hàng — lần sau đọc `sua_luc` mới do lần trước trả về, không tự đá nhau 409.
    tickBuocNoiBo: (so: number, buoc: number, xong: boolean): Promise<boolean> => {
      const truoc = hang_doi_tick.get(so) ?? Promise.resolve(true);
      const lam = truoc.catch(() => true).then(async () => {
        const cu = qc.getQueryData<ThongTinViec>(KHOA.thongTin(so));
        const tu = cu?.mo_ta_noi_bo?.sua_luc;
        if (!cu || !cu.mo_ta_noi_bo || !tu) { thongBao('Chưa tải xong mô tả — thử lại sau giây lát.', 'loi'); return false; }
        const mo_ta = cu.mo_ta_noi_bo;
        qc.setQueryData<ThongTinViec>(KHOA.thongTin(so), { ...cu, mo_ta_noi_bo: { ...mo_ta, noi_dung: datMucChecklist(mo_ta.noi_dung, buoc, xong) } });
        try {
          const kq = await api.patch<{ sua_luc: string; noi_dung: string }>(`viec/${so}/thong-tin`, { buoc, xong, tu });
          const toi = qc.getQueryData<Toi | null>(KHOA.toi);
          qc.setQueryData<ThongTinViec>(KHOA.thongTin(so), (c) => c && c.mo_ta_noi_bo ? { ...c, mo_ta_noi_bo: { ...c.mo_ta_noi_bo, noi_dung: kq.noi_dung, sua_luc: kq.sua_luc, ...(toi ? { sua_boi: { id: toi.id, ten: toi.ten } } : {}) } } : c);
          return true;
        } catch (e) {
          thongBao(thongDiepLoi(e), 'loi');
          qc.invalidateQueries({ queryKey: KHOA.thongTin(so) });
          return false;
        }
      });
      hang_doi_tick.set(so, lam);
      return lam;
    },
    // Đóng / mở lại một việc theo số (tick trong checklist: việc con có thể không nằm trong danh sách đang tải).
    // Hoàn tác chỉ khi việc có trong danh sách đã tải (cần nhãn hiện có để kiểm xung đột); không có thì làm như cũ, không nút.
    datDongMo: async (so: number, dong_lai: boolean, cha?: number) => {
      const cu = qc.getQueryData<IssueGh[]>(KHOA.issues)?.find((x) => x.number === so);
      const nhan_cu = cu?.labels.map((l) => l.name) ?? [];
      const muc = cu ? taoMuc(so, { nhan: nhan_cu, mo: cu.state === 'open' }, { nhan: nhan_cu, mo: !dong_lai }) : null;
      suaTrongCache(qc, so, (i) => ({ ...i, state: dong_lai ? 'closed' : 'open', closed_at: dong_lai ? new Date().toISOString() : null }));
      await chay(qc, () => gh.patch(`repos/${KHO}/issues/${so}`, dong_lai ? { state: 'closed', state_reason: 'completed' } : { state: 'open' }), undefined,
        muc ? () => choHoanTac(`${dong_lai ? 'đóng' : 'mở lại'} #${so}`, [muc]) : undefined);
      if (cha) qc.invalidateQueries({ queryKey: KHOA.con(cha) });
    },
    binhLuan,
    suaBinhLuan,
    xoaBinhLuan,
    datTheoDoi,
    doiTieuDiem,
    // 🤖 AI làm: đưa nhãn về dạng executor nhận (nhanChoAi) + comment + bắn repository_dispatch (tiếng chuông).
    // Executor chạy trên máy owner (`drk vtask`) đọc NHÃN; dispatch chỉ để sau này có executor Actions.
    chayXuLy: async (v: Viec) => {
      const kq = nhanChoAi(v.nhan);
      if ('loi' in kq) { thongBao(kq.loi, 'loi'); return; }
      await datNhan(v, kq.nhan);
      const loi: string[] = [];
      await binhLuan(v.so, '🤖 **Giao AI làm.** Executor nhận việc theo `AGENTS.md` (§2–4): nạp kỹ năng từ `UseAI/agent/skill-index.md`, hồ sơ brand nếu có nhãn `brand:`, nộp kết quả rồi chuyển `tt:cho-duyet`. Chạy trên máy: `drk vtask ' + v.so + '`.')
        .catch(() => loi.push('comment'));
      await gh.post(`repos/${KHO}/dispatches`, { event_type: 'vtask-xu-ly', client_payload: { issue: v.so } }).catch(() => loi.push('dispatch'));
      if (loi.length) thongBao(`#${v.so} đã vào hàng đợi AI, nhưng lỗi ${loi.join(' + ')} — nhãn vẫn đúng, executor vẫn nhận.`, 'loi');
      else thongBao(`#${v.so} → hàng đợi AI (tt:san-sang).`);
    },
    // Rút khỏi hàng đợi AI (chưa ai nhận): về inbox.
    huyAi: (v: Viec) => datNhan(v, thayTruc(v.nhan, 'tt:', 'tt:inbox'), `#${v.so} đã rút khỏi hàng đợi AI.`),
    // Hàng loạt: mỗi việc qua nhanChoAi; việc không giao được thì bỏ qua và đếm.
    aiLamHangLoat: async (ds: Viec[]) => {
      let xong = 0, bo = 0;
      for (const v of ds) {
        const kq = nhanChoAi(v.nhan);
        if ('loi' in kq || !v.mo) { bo++; continue; }
        try {
          await gh.patch(`repos/${KHO}/issues/${v.so}`, { labels: kq.nhan });
          suaTrongCache(qc, v.so, (i) => ({ ...i, labels: kq.nhan.map((n) => i.labels.find((l) => l.name === n) || nhanGh(n)) }));
          xong++;
        } catch { bo++; }
      }
      qc.invalidateQueries({ queryKey: KHOA.issues });
      thongBao(`${xong} việc vào hàng đợi AI` + (bo ? ` · ${bo} bỏ qua (mục tiêu / ai:dung-cham / lỗi)` : ''), bo && !xong ? 'loi' : undefined);
    },
    duyet: (v: Viec) => dong(v, `#${v.so} đã duyệt & đóng.`),
    traLai: async (v: Viec, gop_y: string) => {
      await binhLuan(v.so, `↩ **Trả lại để làm tiếp:**\n\n${gop_y || '(không ghi chú)'}`);
      return datNhan(v, thayTruc(v.nhan, 'tt:', 'tt:san-sang'), `#${v.so} đã trả lại.`, `trả lại #${v.so} (bình luận vẫn giữ)`);
    },
    // im_lang (tạo nhiều dòng — HopTaoViec): không báo "Đã tạo" từng việc và NÉM lỗi tạo issue cho nơi gọi ghi vào đúng dòng
    // (lô không dừng); nơi gọi tự báo tổng kết và tự làm mới KHOA.issues MỘT lần cuối lô (khỏi tải lại cả kho việc mỗi dòng).
    taoViec: async (dl: { tieu_de: string; than: string; nhan: string[]; tags: string[]; doi_tac?: string[]; ky?: string | null; nguoi: number[]; chay_ngay: boolean; nguoi_duyet?: number | null }, tuy_chon?: { im_lang?: boolean }) => {
      const tag = await damBaoNhan('tag', dl.tags);
      const dt = await damBaoNhan('doi-tac', dl.doi_tac || []);
      const ky = dl.ky ? await damBaoNhan('ky', [dl.ky]) : [];
      const goi = () => gh.post<IssueGh>(`repos/${KHO}/issues`, { title: dl.tieu_de, body: dl.than, labels: [...dl.nhan, ...tag, ...dt, ...ky] });
      const moi = tuy_chon?.im_lang ? await goi() : await chay(qc, goi);
      if (!moi) return undefined;
      if (dl.nguoi.length) {
        await api.put(`viec/${moi.number}/phu-trach`, { nguoi: dl.nguoi }).catch((e) => thongBao(thongDiepLoi(e), 'loi'));
        qc.invalidateQueries({ queryKey: KHOA.phuTrach });
      }
      if (dl.nguoi_duyet) {
        await api.put(`viec/${moi.number}/nguoi-duyet`, { nguoi: dl.nguoi_duyet }).catch((e) => thongBao(thongDiepLoi(e), 'loi'));
        qc.invalidateQueries({ queryKey: KHOA.nguoiDuyet });
      }
      if (dl.chay_ngay) {
        await gh.post(`repos/${KHO}/dispatches`, { event_type: 'vtask-xu-ly', client_payload: { issue: moi.number } }).catch(() => null);
      }
      if (!tuy_chon?.im_lang) thongBao(`Đã tạo #${moi.number}.`);
      return moi;
    },
  };

  // Nhãn tự do → `<tiền tố>:<slug>`; tạo nhãn còn thiếu, 422 = đã có → bỏ qua. `ky` giữ nguyên mã quý.
  async function damBaoNhan(tien_to: 'tag' | 'doi-tac' | 'ky' | 'tai-san', gia_tri: string[]): Promise<string[]> {
    const sach = [...new Set(tien_to === 'ky' ? gia_tri.filter((k) => /^\d{4}-q[1-4]$/.test(k)) : gia_tri.map(slugTag).filter(Boolean))];
    const co_san = new Set((qc.getQueryData<NhanGh[]>(KHOA.nhanRepo) || []).map((l) => l.name));
    const thieu = sach.filter((t) => !co_san.has(`${tien_to}:${t}`));
    const kieu = { tag: ['c5cad3', 'Tag tự do'], 'doi-tac': ['8fb3e8', 'Đối tác'], ky: ['f6c28b', 'Chu kỳ OKR'], 'tai-san': ['b4e3c1', 'Tài sản thương hiệu'] }[tien_to];
    for (const t of thieu) {
      await gh.post(`repos/${KHO}/labels`, { name: `${tien_to}:${t}`, color: kieu[0], description: `${kieu[1]} (tạo từ giao diện VTask)` }).catch(() => null);
    }
    if (thieu.length) qc.invalidateQueries({ queryKey: KHOA.nhanRepo });
    return sach.map((t) => `${tien_to}:${t}`);
  }

  // Đóng việc (nút Xong, kéo vào cột Xong, Duyệt & đóng) — luôn cho hoàn tác (mở lại).
  function dong(v: Viec, bao = `#${v.so} đã đóng.`) {
    suaTrongCache(qc, v.so, (i) => ({ ...i, state: 'closed', closed_at: new Date().toISOString() }));
    const muc = taoMuc(v.so, anhGh(v), { nhan: v.nhan, mo: false });
    return chay(qc, () => gh.patch(`repos/${KHO}/issues/${v.so}`, { state: 'closed', state_reason: 'completed' }), bao,
      () => choHoanTac(`đóng #${v.so}`, [muc], bao));
  }
}

export function useDangXuat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('dang-xuat'),
    onSettled: () => {
      // Đặt toi = null TRƯỚC: App (đang bám đúng truy vấn này) chuyển sang màn đăng nhập và gỡ cả cây component đang
      // đọc toi.data!, RỒI mới xoá phần còn lại. Làm ngược lại (clear() rồi mới đặt null) thì observer của App còn bám
      // truy vấn cũ đã bị xoá nên không hay biết gì, trong khi component con vẽ lại (do mutation đổi trạng thái), đọc
      // truy vấn mới (null) → crash "reading 'vai_tro'" → trang trắng. Lỗi có từ trước, bắt được 28/09.
      qc.setQueryData(KHOA.toi, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== KHOA.toi[0] });
      xoaBoNhoEtag();
    },
  });
}
